import React, { useState } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  Alert, 
  Typography, 
  Space, 
  Progress,
  List,
  message 
} from 'antd';
import { 
  LockOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone
} from '@ant-design/icons';
import { usePatientPortal } from '../context/PatientPortalContext';

const { Title, Text } = Typography;

const PatientPortalPasswordChange = () => {
  const { getPortalAxios } = usePatientPortal();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    requirements: {
      minLength: false,
      hasUppercase: false,
      hasLowercase: false,
      hasNumber: false,
      hasSpecialChar: false
    }
  });

  // Password strength validation
  const validatePasswordStrength = (password) => {
    const result = {
      score: 0,
      requirements: {
        minLength: false,
        hasUppercase: false,
        hasLowercase: false,
        hasNumber: false,
        hasSpecialChar: false
      }
    };

    if (!password) return result;

    // Check minimum length
    if (password.length >= 8) {
      result.requirements.minLength = true;
      result.score += 1;
    }

    // Check for uppercase letter
    if (/[A-Z]/.test(password)) {
      result.requirements.hasUppercase = true;
      result.score += 1;
    }

    // Check for lowercase letter
    if (/[a-z]/.test(password)) {
      result.requirements.hasLowercase = true;
      result.score += 1;
    }

    // Check for number
    if (/[0-9]/.test(password)) {
      result.requirements.hasNumber = true;
      result.score += 1;
    }

    // Check for special character
    if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      result.requirements.hasSpecialChar = true;
      result.score += 1;
    }

    return result;
  };

  const handlePasswordChange = (e) => {
    const password = e.target.value;
    const strength = validatePasswordStrength(password);
    setPasswordStrength(strength);
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const portalAxios = getPortalAxios();
      await portalAxios.patch('/portal/change-password', {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword
      });

      setSuccess(true);
      form.resetFields();
      setPasswordStrength({
        score: 0,
        requirements: {
          minLength: false,
          hasUppercase: false,
          hasLowercase: false,
          hasNumber: false,
          hasSpecialChar: false
        }
      });
      message.success('Password changed successfully!');
    } catch (err) {
      console.error('Password change error:', err);
      if (err.response?.status === 401) {
        setError('Current password is incorrect');
      } else if (err.response?.status === 400) {
        setError(err.response.data.message || 'Password does not meet security requirements');
      } else {
        setError('Failed to change password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength.score <= 1) return '#ff4d4f';
    if (passwordStrength.score <= 3) return '#faad14';
    return '#52c41a';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength.score <= 1) return 'Weak';
    if (passwordStrength.score <= 3) return 'Medium';
    return 'Strong';
  };

  const requirementsList = [
    { key: 'minLength', text: 'At least 8 characters' },
    { key: 'hasUppercase', text: 'At least one uppercase letter' },
    { key: 'hasLowercase', text: 'At least one lowercase letter' },
    { key: 'hasNumber', text: 'At least one number' },
    { key: 'hasSpecialChar', text: 'At least one special character' }
  ];

  return (
    <Card 
      title={
        <Space>
          <LockOutlined />
          <Title level={4} style={{ margin: 0 }}>Change Password</Title>
        </Space>
      }
      style={{ maxWidth: 600, margin: '0 auto' }}
    >
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 24 }}
          closable
          onClose={() => setError('')}
        />
      )}

      {success && (
        <Alert
          message="Success"
          description="Your password has been changed successfully."
          type="success"
          showIcon
          style={{ marginBottom: 24 }}
          closable
          onClose={() => setSuccess(false)}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        disabled={loading}
      >
        <Form.Item
          name="currentPassword"
          label="Current Password"
          rules={[
            { required: true, message: 'Please enter your current password' }
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Enter your current password"
            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
          />
        </Form.Item>

        <Form.Item
          name="newPassword"
          label="New Password"
          rules={[
            { required: true, message: 'Please enter a new password' },
            { min: 8, message: 'Password must be at least 8 characters' },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                const strength = validatePasswordStrength(value);
                if (strength.score < 4) {
                  return Promise.reject(new Error('Password does not meet security requirements'));
                }
                return Promise.resolve();
              }
            }
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Enter your new password"
            onChange={handlePasswordChange}
            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
          />
        </Form.Item>

        {form.getFieldValue('newPassword') && (
          <div style={{ marginBottom: 24 }}>
            <Text strong>Password Strength: </Text>
            <Text style={{ color: getPasswordStrengthColor() }}>
              {getPasswordStrengthText()}
            </Text>
            <Progress
              percent={(passwordStrength.score / 5) * 100}
              strokeColor={getPasswordStrengthColor()}
              showInfo={false}
              size="small"
              style={{ marginTop: 8 }}
            />
            
            <div style={{ marginTop: 16 }}>
              <Text strong>Requirements:</Text>
              <List
                size="small"
                dataSource={requirementsList}
                renderItem={item => (
                  <List.Item style={{ padding: '4px 0', border: 'none' }}>
                    <Space>
                      {passwordStrength.requirements[item.key] ? (
                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                      ) : (
                        <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                      )}
                      <Text 
                        style={{ 
                          color: passwordStrength.requirements[item.key] ? '#52c41a' : '#ff4d4f' 
                        }}
                      >
                        {item.text}
                      </Text>
                    </Space>
                  </List.Item>
                )}
              />
            </div>
          </div>
        )}

        <Form.Item
          name="confirmPassword"
          label="Confirm New Password"
          dependencies={['newPassword']}
          rules={[
            { required: true, message: 'Please confirm your new password' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Passwords do not match'));
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Confirm your new password"
            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
          />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            style={{ width: '100%' }}
            disabled={passwordStrength.score < 4}
          >
            Change Password
          </Button>
        </Form.Item>
      </Form>

      <Alert
        message="Security Tips"
        description={
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>Use a unique password that you don't use for other accounts</li>
            <li>Consider using a password manager to generate and store secure passwords</li>
            <li>Never share your password with anyone</li>
            <li>Log out when using shared or public computers</li>
          </ul>
        }
        type="info"
        showIcon
        style={{ marginTop: 16 }}
      />
    </Card>
  );
};

export default PatientPortalPasswordChange;
